import * as React from "react"
import {
  Link,
  createFileRoute,
  getRouteApi,
  useNavigate,
} from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import { BookmarkPlus, List, Map as MapIcon, SearchX } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { PropertyCard } from "@/components/property-display"
import { PropertyMap } from "@/components/property-map"
import { PropertySearchForm } from "@/components/search-form"
import {
  createSavedSearchFn,
  normalizeSearchFromUrl,
  searchPropertiesFn,
} from "@/lib/app.functions"
import { useLocale } from "@/lib/i18n"
import type { PropertySearch } from "@/lib/types"

const rootRoute = getRouteApi("__root__")

export const Route = createFileRoute("/search")({
  validateSearch: (search) => normalizeSearchFromUrl(search),
  loaderDeps: ({ search }) => search,
  loader: ({ deps }) => searchPropertiesFn({ data: deps }),
  head: () => ({
    meta: [
      {
        title: "Property search · Harbor & Pine Realty",
      },
    ],
  }),
  component: SearchPage,
})

function SearchPage() {
  const result = Route.useLoaderData()
  const search = Route.useSearch()
  const viewer = rootRoute.useLoaderData()
  const navigate = useNavigate()
  const [mapVisible, setMapVisible] = React.useState(true)
  const [activeId, setActiveId] = React.useState<number>()
  const { t } = useLocale()

  const updateSearch = (next: PropertySearch) =>
    navigate({ to: "/search", search: next })

  return (
    <div className="field-container py-10">
      <div className="flex flex-wrap items-end justify-between gap-5 border-b-2 border-foreground pb-6">
        <div>
          <p className="field-label">{t("cataloguePropertyFinder")}</p>
          <h1 className="field-heading mt-2">{t("propertySearch")}</h1>
          <p className="mt-3 text-sm text-muted-foreground" aria-live="polite">
            {result.total} {result.total === 1 ? t("listing") : t("listings")}{" "}
            {t(result.total === 1 ? "matchSearchSingular" : "matchSearch")}
          </p>
        </div>
        <div className="flex gap-2">
          {viewer ? <SaveSearchDialog search={search} /> : null}
          <Button
            type="button"
            variant="outline"
            className="field-button"
            onClick={() => setMapVisible((value) => !value)}
          >
            {mapVisible ? <List /> : <MapIcon />}
            {mapVisible ? t("listOnly") : t("showMap")}
          </Button>
        </div>
      </div>

      <div className="mt-7 grid items-start gap-6 xl:grid-cols-[310px_minmax(0,1fr)]">
        <aside className="border border-border bg-white p-5 xl:sticky xl:top-24">
          <p className="field-label mb-5">{t("refineSearch")}</p>
          <PropertySearchForm
            key={JSON.stringify(search)}
            defaults={search}
            onSearch={updateSearch}
          />
          <Button
            variant="ghost"
            className="field-button mt-3 w-full"
            nativeButton={false}
            render={<Link to="/search" search={{}} />}
          >
            {t("clearFilters")}
          </Button>
        </aside>

        <section className="min-w-0">
          {mapVisible && result.items.length > 0 ? (
            <PropertyMap
              properties={result.items}
              activeId={activeId}
              onActivate={setActiveId}
              className="mb-6 min-h-[430px]"
            />
          ) : null}

          {result.items.length > 0 ? (
            <div className="grid gap-5 2xl:grid-cols-2">
              {result.items.map((property) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  signedIn={Boolean(viewer)}
                  className={
                    activeId === property.id
                      ? "ring-2 ring-primary ring-offset-2"
                      : ""
                  }
                />
              ))}
            </div>
          ) : (
            <div className="grid min-h-96 place-items-center border border-dashed border-border bg-white p-8 text-center">
              <div>
                <SearchX className="mx-auto size-10 text-muted-foreground" />
                <h2 className="mt-4 font-heading text-3xl uppercase">
                  {t("noListingsFound")}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t("widenCriteria")}
                </p>
              </div>
            </div>
          )}

          {result.pages > 1 ? (
            <nav
              aria-label="Search pages"
              className="mt-8 flex items-center justify-between border-t-2 border-foreground pt-5"
            >
              <Button
                variant="outline"
                className="field-button"
                disabled={result.page <= 1}
                onClick={() =>
                  void navigate({
                    to: "/search",
                    search: { ...search, page: result.page - 1 },
                  })
                }
              >
                {t("previous")}
              </Button>
              <span className="field-label">
                {t("page")} {result.page} / {result.pages}
              </span>
              <Button
                variant="outline"
                className="field-button"
                disabled={result.page >= result.pages}
                onClick={() =>
                  void navigate({
                    to: "/search",
                    search: { ...search, page: result.page + 1 },
                  })
                }
              >
                {t("next")}
              </Button>
            </nav>
          ) : null}
        </section>
      </div>
    </div>
  )
}

function SaveSearchDialog({ search }: { search: PropertySearch }) {
  const saveSearch = useServerFn(createSavedSearchFn)
  const [open, setOpen] = React.useState(false)
  const [pending, setPending] = React.useState(false)
  const { t } = useLocale()

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setPending(true)
    const form = new FormData(event.currentTarget)
    try {
      await saveSearch({
        data: { name: String(form.get("name") ?? ""), filters: search },
      })
      setOpen(false)
      toast.success("Search saved to your workspace.")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to save.")
    } finally {
      setPending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button variant="outline" className="field-button">
            <BookmarkPlus />
            {t("saveSearch")}
          </Button>
        }
      />
      <DialogContent className="rounded-none">
        <DialogHeader>
          <DialogTitle className="text-3xl uppercase">
            {t("nameSearch")}
          </DialogTitle>
          <DialogDescription>{t("saveSearchDescription")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="mt-3 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="search-name" className="field-label">
              {t("savedSearchName")}
            </Label>
            <Input
              id="search-name"
              name="name"
              required
              minLength={2}
              placeholder="North Quay two-bedroom"
              className="field-input"
            />
          </div>
          <Button
            type="submit"
            className="field-button w-full"
            disabled={pending}
          >
            {pending ? t("saving") : t("saveSearchAction")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
